/**
 * Scroller — единственный способ завести область с прокруткой.
 *
 * Оборачивает overlayscrollbars: нативная прокрутка остаётся нативной (колесо,
 * клавиатура, `scrollTop`, `scrollIntoView`, sticky, виртуализация — всё живое),
 * подменяется только отрисовка полосы. Полоса накладывается поверх содержимого,
 * поэтому не резервирует ширину и не даёт скачка вёрстки. Видна она только во
 * время прокрутки и быстро гаснет после неё; при входе указателя ненадолго
 * подмигивает — «здесь листается» (владелец, 27.10.2026: полоса, висевшая всё
 * время, пока курсор над областью, мешала читать).
 *
 * Разметка ровно два узла и не меняется от опций:
 *
 *   <div className={className}>            ← хост: коробка + padding
 *     <div className={viewportClassName}>  ← viewport: прокрутка + раскладка содержимого
 *
 * Правило миграции ровно одно, но с исключением:
 *
 * - На хосте остаётся коробка — размер, `flex`/`grid`-участие, `margin`, фон,
 *   радиус, тень — **и `padding`**.
 * - В `viewportClassName` переезжает то, что раскладывает содержимое:
 *   `display`, `flex-direction`, `gap`, `align-*`, `grid-template-*`, а также
 *   `mask-image` (на хосте градиент растворял бы заодно и полосу прокрутки —
 *   она соседний узел, а не часть содержимого). Оставленные на хосте, эти
 *   свойства применятся к единственному ребёнку-viewport, а не к элементам
 *   списка.
 *
 * **`padding` в `viewportClassName` писать бесполезно.** overlayscrollbars
 * читает padding хоста и сам проставляет его viewport'у inline вместе с
 * компенсирующими `margin`/`left`/`top`/`width` — так содержимое прокручивается
 * под отступ, как у нативного скроллера. Всё, что написано про padding у
 * viewport в CSS, этим inline-значением затирается.
 *
 * Padding хоста при этом можно менять на ходу — хоть числом, хоть
 * css-переменной с `<html>`: Scroller следит за своим content box'ом сам и
 * пересобирает inline-снимок (см. `ResizeObserver` в эффекте инициализации).
 *
 * `overflow` в CSS обеих частей не пишем: осями управляет проп `axis`.
 *
 * @use любая область с прокруткой — единственный способ её завести.
 */
import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  ComponentPropsWithoutRef,
  ElementType,
  MutableRefObject,
  ReactNode,
  Ref,
} from 'react';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import type { PartialOptions } from 'overlayscrollbars';
import 'overlayscrollbars/overlayscrollbars.css';
import './scroller.css';
import { cn } from '@/shared/lib/cn';
import styles from './Scroller.module.css';

export type ScrollerAxis = 'y' | 'x' | 'both';

const PEEK_MS = 900;

export type ScrollerAutoHide = 'leave' | 'scroll' | 'move' | 'never';

export type ScrollerElement =
  | 'div'
  | 'main'
  | 'aside'
  | 'nav'
  | 'section'
  | 'ul'
  | 'ol'
  | 'pre';

export interface ScrollerProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onScroll'> {
  element?: ScrollerElement;
  axis?: ScrollerAxis;
  autoHide?: ScrollerAutoHide;
  viewportClassName?: string;
  viewportProps?: Omit<ComponentPropsWithoutRef<'div'>, 'className' | 'ref' | 'children'>;
  viewportRef?: Ref<HTMLDivElement>;
  onScroll?: (event: Event) => void;
  overscroll?: 'contain' | 'auto';
  page?: boolean;
  children?: ReactNode;
}

const OVERFLOW: Record<ScrollerAxis, PartialOptions['overflow']> = {
  y: { x: 'hidden', y: 'scroll' },
  x: { x: 'scroll', y: 'hidden' },
  both: { x: 'scroll', y: 'scroll' },
};

const NATIVE_SCROLLBARS =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: none) and (pointer: coarse)').matches;

export const Scroller = forwardRef<HTMLElement, ScrollerProps>(function Scroller(
  {
    element = 'div',
    axis = 'y',
    autoHide = 'scroll',
    className,
    viewportClassName,
    viewportProps,
    viewportRef,
    onScroll,
    overscroll = 'contain',
    page = false,
    children,
    ...rest
  },
  ref,
) {
  const Host = element as ElementType;
  const hostRef = useRef<HTMLElement | null>(null);
  const viewportElRef = useRef<HTMLDivElement | null>(null);

  const onScrollRef = useRef(onScroll);
  onScrollRef.current = onScroll;

  const options = useMemo<PartialOptions>(
    () => ({
      overflow: OVERFLOW[axis],
      showNativeOverlaidScrollbars: NATIVE_SCROLLBARS,
      scrollbars: {
        theme: 'os-theme-pridex',
        autoHide,
        autoHideDelay: 500,
        clickScroll: true,
      },
    }),
    [axis, autoHide],
  );

  const events = useMemo(
    () => ({ scroll: (_instance: unknown, event: Event) => onScrollRef.current?.(event) }),
    [],
  );

  const [initialize, getInstance] = useOverlayScrollbars({ options, events });

  useEffect(() => {
    const host = hostRef.current;
    const viewport = viewportElRef.current;
    if (!host || !viewport) return;
    if (page) return;
    initialize({ target: host, elements: { viewport, padding: false, content: false } });

    let lastPaddingTop: string | null = null;
    const ro = new ResizeObserver(() => {
      const paddingTop = getComputedStyle(host).paddingTop;
      if (paddingTop === lastPaddingTop) return;
      lastPaddingTop = paddingTop;
      getInstance()?.update(true);
    });
    ro.observe(host, { box: 'content-box' });

    return () => {
      ro.disconnect();
      getInstance()?.destroy();
    };
  }, [initialize, getInstance, page]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || page || autoHide !== 'scroll' || NATIVE_SCROLLBARS) return;
    let timer: number | undefined;
    const peek = () => {
      host.setAttribute('data-os-peek', '');
      window.clearTimeout(timer);
      timer = window.setTimeout(() => host.removeAttribute('data-os-peek'), PEEK_MS);
    };
    host.addEventListener('pointerenter', peek);
    return () => {
      host.removeEventListener('pointerenter', peek);
      window.clearTimeout(timer);
      host.removeAttribute('data-os-peek');
    };
  }, [autoHide, page]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || page || NATIVE_SCROLLBARS) return;
    const gestures = ['pointermove', 'pointerdown', 'wheel', 'touchstart', 'keydown'] as const;
    const wake = () => {
      host.removeAttribute('data-os-quiet');
      gestures.forEach((g) => host.removeEventListener(g, wake));
    };
    host.setAttribute('data-os-quiet', '');
    gestures.forEach((g) => host.addEventListener(g, wake, { passive: true }));
    return wake;
  }, [page]);

  const setHost = useCallback(
    (node: HTMLElement | null) => {
      hostRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLElement | null>).current = node;
    },
    [ref],
  );

  const setViewport = useCallback(
    (node: HTMLDivElement | null) => {
      viewportElRef.current = node;
      if (typeof viewportRef === 'function') viewportRef(node);
      else if (viewportRef) (viewportRef as MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [viewportRef],
  );

  return (
    <Host {...rest} ref={setHost} className={cn(styles.host, page && styles.hostPage, className)}>
      <div
        {...viewportProps}
        ref={setViewport}
        className={cn(
          styles.viewport,
          page && styles.viewportPage,
          overscroll === 'contain' && styles.overscrollContain,
          viewportClassName,
        )}
      >
        {children}
      </div>
    </Host>
  );
});
